/// <reference path='common.ts' />
/// <reference path='fetch.ts' />

/**
 * 汎用的なデータとエレメントを連携させる機能です。data-bind属性にデータをJSON形式で設定します。
 * 属性およびコンテンツにおいて{{}}で囲んだ文字列をスクリプトとして実行し戻り値と置換します。
 * スクリプトではdata-bindで指定されたパラメータを変数として使用できます。
 * 属性に指定された場合で戻り値がfalseの場合は属性自身を除去、trueの場合は値なしとします。
 *
 * 属性
 * data-bind: 参照するデータ
 * data-bind-extend: 先祖エレメントのdata-bind属性と結合する
 * data-bind-default: 参照データのデフォルト値（data-bindと同じエレメントの場合に結合される）
 * data-bind-fetch: URLからフェッチした内容をdata-bindに割り当てる
 * data-bind-fetch-arg: フェッチした内容を設定する変数名（省略時は展開する）
 * data-bind-fetch-merge: フェッチ結果を元のdeta-bindとマージする
 * data-bind-fetch-click: フェッチが完了した際に当該エレメントに対してクリックイベントを発生させる
 * data-bind-fetch-redirect: フェッチに成功した際に遷移するURL
 * data-bind-method: フェッチする際のメソッド（省略時はGET）
 * data-bind-enctype: フェッチする際に送信するContent-Type（GETメソッドの場合はapplication/x-www-form-urlencoded、それ以外場合はapplication/jsonがデフォルト）
 * data-bind-form: フェッチする際に付与するパラメータが格納されているフォームのセレクタ（値が空の場合は親フォーム）フェッチしない場合はパラメータが値として利用される
 * data-bind-cache-use: キャッシュした内容をフェッチの代わりに使用する場合（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-bind-cache-set: フェッチした内容をキャッシュする（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-bind-cache-clear: クリックした際もしくはフェッチに成功した際にキャッシュをクリアする（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-bind-each: 属性値に指定されたリストの数だけ子エレメントをコピーし、それぞれのエレメントのdata-bind属性に値を設定する（indexパラメータにインデックスを付ける）
 * data-bind-each-before: 繰り返しの対象とせずに繰り返しの前に表示されます
 * data-bind-each-after: 繰り返しの対象とせずに繰り返しの後に表示されます
 * data-bind-each-arg: 繰り返しエレメントのdata-bind属性に設定する値の変数名（省略時は展開する）
 * data-bind-if: 表示判定（戻り値がfalseかnullなら非表示、その他なら元の表示状態とする）
 * data-bind-if-false: data-bind-ifで非表示の場合（変更しないで下さい）
 * data-bind-invalid: 当該エレメント以下のエレメントのbind処理を無効にする
 * data-bind-transfer: 属性名を付け替える（変換元属性名と変換先属性名をスペースで区切る、変換元属性値にスクリプトが含まれている場合はスキップする）
 * data-bind-fetch-current: 最後に処理したdata-bind-fetch属性の値（変更しないで下さい）
 * data-bind-copy: フェッチした内容をコピーして設定するエレメントのセレクタ
 *
 * イベント
 * fetch: 通信が完了した場合（detailはrequestとresponseのPromise）
 * fetch-error: 通信中にエラーが発生した場合（detailはrequestとエラー内容）
 * change-bind: data-bind属性の値が変更された場合
 */
class HaoriBind {
  /** イベントの発生を抑制するエレメントのセット */
  private static stopEventElements = new Set<Element>();

  /** エレメントに対する変更前のコンテンツ */
  private static srcContents = new Map<ChildNode, string>();

  /** エレメントに対するアトリビュート名に対する変更前のアトリビュート値 */
  private static srcAttributes = new Map<Element, Map<string, string>>();

  /** エレメントに対するアトリビュート名に対する取り除かれたアトリビュートのセット */
  private static removeAttributes = new Map<Element, Set<string>>();

  /** ifエレメントに対する中身 */
  private static srcIfElements = new Map<Element, Array<Node>>();

  /** eachエレメントに対するバインドオブジェクトの文字列 */
  private static eachBindStrings = new Map<Element, string>();

  /** eachエレメントに対するスクリプト */
  private static eachScripts = new Map<Element, string>();

  /** 文字列を変更中のノードのセット */
  private static contentChangeNodes = new Set<Node>();

  /** アトリビュートを変更中のエレメントに対するアトリビュート名のマップ */
  private static attributeChangeNodes = new Map<Element, Set<string>>();

  /** エレメントに対する繰り返し前の初期HTML */
  private static srcBeforeEachs = new Map<Element, string>();

  /** エレメントに対するテンプレートエレメントのリスト */
  private static srcEachs = new Map<Element, Array<Element>>();

  /** アトリビュート変更オブザーバ */
  static attributeObserver = new MutationObserver((mutations) => {
    this.attributeChanged(mutations);
  });

  /** テキスト変更オブザーバ */
  static contentObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (this.contentChangeNodes.has(mutation.target)) {
        this.contentChangeNodes.delete(mutation.target);
        return;
      }
      let parent = mutation.target.parentElement;
      if (parent != null) {
        this.doScriptContent(parent, false);
      }
    });
  });

  /** エレメント変更オブザーバ */
  static childObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType == Node.ELEMENT_NODE) {
          this.bind(<Element>node);
        }
      });
    });
  });

  /**
   * ノードを複製します。Node.cloneNodeではなくこちらを使用してください。
   *
   * @param node 対象エレメント
   */
  static clone(node: Node): Node {
    let cloned = node.cloneNode();
    if (node.nodeType == Node.ELEMENT_NODE) {
      let element = <Element>node;
      let cloneElement = <Element>cloned;
      if (this.stopEventElements.has(element)) {
        this.stopEventElements.add(element);
      }
      if (this.srcAttributes.has(element)) {
        let srcMap = this.srcAttributes.get(element);
        this.srcAttributes.set(cloneElement, new Map(srcMap));
      }
      if (this.removeAttributes.has(element)) {
        let srcSet = this.removeAttributes.get(element);
        this.removeAttributes.set(cloneElement, new Set(srcSet));
      }
      if (this.srcIfElements.has(element)) {
        let cloneNodes = new Array<Node>();
        this.srcIfElements.get(element)?.forEach((node) => {
          cloneNodes.push(HaoriBind.clone(node));
        });
        this.srcIfElements.set(cloneElement, cloneNodes);
      }
      if (this.eachBindStrings.has(element)) {
        let src = this.eachBindStrings.get(element);
        this.eachBindStrings.set(cloneElement, src!);
      }
      if (this.eachScripts.has(element)) {
        let src = this.eachScripts.get(element);
        this.eachScripts.set(cloneElement, src!);
      }
      if (this.srcEachs.has(element)) {
        let src = this.srcEachs.get(element);
        this.srcEachs.set(cloneElement, src!);
      }
      if (this.srcBeforeEachs.has(element)) {
        let src = this.srcBeforeEachs.get(element);
        this.srcBeforeEachs.set(cloneElement, src!);
      }
    }
    for (let i = 0; i < node.childNodes.length; i++) {
      let child = node.childNodes[i];
      if (this.srcContents.has(child)) {
        let content = this.srcContents.get(child);
        this.srcContents.set(cloned.childNodes[i], content!);
      }
    }
    node.childNodes.forEach((child) => {
      cloned.appendChild(HaoriBind.clone(child));
    });
    return cloned;
  }

  /**
   * アトリビュートが変更された場合の処理を行います。
   *
   * @param mutations 変更情報のリスト
   */
  private static attributeChanged(mutations: MutationRecord[]) {
    mutations.forEach((mutation) => {
      let element = <HTMLElement>mutation.target;
      let set = this.attributeChangeNodes.get(element);
      if (set != null && mutation.attributeName && set.has(mutation.attributeName)) {
        set.delete(mutation.attributeName);
        return;
      }
      if (mutation.attributeName != null && element.getAttribute(mutation.attributeName) == mutation.oldValue) {
        // 変更なし
        return;
      }
      if (element.closest('html') == null) {
        // DOMに組み込まれていないエレメントはスキップする
        return;
      }
      let transfers: Array<string> | null = null;
      if (element.hasAttribute('data-bind-transfer')) {
        transfers = (<string>element.getAttribute('data-bind-transfer')).split(' ');
        if (transfers.length != 2) {
          transfers = null;
          console.error('data-bind-transfer属性の値は（変換元属性名）" "（変換先属性名）で記述してください');
        }
      }
      switch (mutation.attributeName) {
        case 'data-bind':
        case 'data-bind-default':
          this.bind(element);
          if (this.stopEventElements.has(element)) {
            this.stopEventElements.delete(element);
          } else {
            if (element.hasAttribute('data-bind')) {
              element.dispatchEvent(
                new CustomEvent('change-bind', {
                  bubbles: true,
                  cancelable: true,
                  composed: true,
                })
              );
            }
          }
          break;
        case 'data-bind-fetch':
          this.doFetch(element);
          break;
        case 'data-bind-each':
          this.doEach(element);
          break;
        case 'data-bind-if':
          this.doIf(element);
          break;
        case 'data-bind-invalid':
          this.bind(element);
          break;
        case 'data-bind-transfer':
        case transfers != null && (<Array<string>>transfers)[0]:
          this.transfer(element);
          break;
        default:
          if (mutation.attributeName != null) {
            this.doScriptAttribute(element, mutation.attributeName, false);
          }
      }
    });
  }

  /**
   * イベントの発生を抑制するエレメントを登録します。
   * イベントが派生するたびに登録し直す必要があります。
   *
   * @param element 対象エレメント
   */
  static setStopEventElements(element: Element) {
    this.stopEventElements.add(element);
  }

  /**
   * 対象の処理必要の有無を取得します。
   *
   * @param element 対象エレメント
   * @return 処理が不要な場合はtrue
   */
  private static isInvalid(element: Element | null) {
    if (element == null) {
      return true;
    }
    if (element.closest('html') == null) {
      // DOMに組み込まれていない場合
      return true;
    }
    if (Haori.hasClosestAttribute(element, 'data-bind-invalid')) {
      return true;
    }
    if (Haori.hasClosestAttribute(element, 'data-bind-if-false')) {
      return true;
    }
    return false;
  }

  /**
   * 対象エレメントを含む子孫エレメントのdata-bind-*およびスクリプト処理を行います。
   *
   * @param element 対象エレメント
   * @return 変更した場合はtrue
   */
  static bind(element: Element): boolean {
    let result = false;
    let currentValue = this.getValue(element);
    if (element.hasAttribute('data-bind-if')) {
      if (this.doIf(element)) {
        result = true;
      }
    }
    if (element.hasAttribute('data-bind-each')) {
      if (this.doEach(element)) {
        result = true;
      }
    }
    if (this.doScriptAttributes(element, true)) {
      result = true;
    }
    if (element.hasAttribute('data-bind-fetch')) {
      if (this.doFetch(element)) {
        result = true;
      }
    }
    if (element.hasAttribute('data-bind-transfer')) {
      if (this.transfer(element)) {
        result = true;
      }
    }
    if (this.doScriptAttributes(element, true)) {
      result = true;
    }
    if (this.doScriptContent(element, true)) {
      result = true;
    }
    for (let i = 0; i < element.children.length; i++) {
      let child = <HTMLElement>element.children[i];
      if (this.bind(child)) {
        result = true;
      }
    }
    if (result && (element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA')) {
      setTimeout(() => {
        // optionのselectedを反映するためにsetTimeoutで判定を遅らせる
        let newValue = this.getValue(element);
        if (currentValue!.indexOf('{{') < 0 && newValue!.indexOf('{{') < 0 && currentValue != newValue) {
          element.setAttribute('data-disabled-change-fetch', ''); // bindでの変更はdata-change-fetchの対象外とする
          element.dispatchEvent(
            new CustomEvent('change', {
              bubbles: true,
              cancelable: true,
              composed: true,
            })
          );
          element.removeAttribute('data-disabled-change-fetch');
        }
      });
    }
    return result;
  }

  /**
   * 対象エレメントのvalue値を取得します。エレメントが存在しない場合はnullを返します。
   *
   * @param element 対象エレメント
   * @return value値の値
   */
  static getValue(element: Element): string | null {
    if (element && element.tagName) {
      switch (element.tagName) {
        case 'INPUT':
          return (<HTMLInputElement>element).value;
        case 'TEXTAREA':
          return (<HTMLTextAreaElement>element).value;
        case 'SELECT':
          let value = (<HTMLSelectElement>element).value;
          if (value == '' && element.querySelector('option[value=""],option:not([value])') == null) {
            // 中身が変わっていてvalueが空文字列になっている場合はvalueを再設定する
            let option = element.querySelector('option[selected]');
            if (option == null) {
              option = element.querySelector('option');
            }
            if (option != null) {
              value = option.getAttribute('value')!;
              (<HTMLSelectElement>element).value = value;
            }
          }
          return value;
      }
    }
    return null;
  }

  /**
   * data-bind-fetchの値からデータを取得してdata-bindに設定します。
   *
   * @param element 処理対象のエレメント
   * @param 変更した場合はtrue
   */
  static doFetch(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      return false;
    }
    let url = element.getAttribute('data-bind-fetch');
    if (url == null) {
      console.error('data-bind-fetch属性が指定されていません', element);
      return false;
    }
    if (url.indexOf('{{') >= 0) {
      // プレースホルダが存在する場合はスキップする
      return false;
    }
    if (element.hasAttribute('data-bind-fetch-current')) {
      if (element.getAttribute('data-bind-fetch-current') == url) {
        // 変更なし
        return false;
      }
    }
    element.setAttribute('data-bind-fetch-current', url);
    let fetch = new HaoriFetch();
    fetch.setParams(element, 'data-bind');
    fetch.bindElements = [element];
    fetch.bindArg = element.getAttribute('data-bind-fetch-arg');
    fetch.bindMerge = element.getAttribute('data-bind-fetch-merge') ? true : null;
    if (element.hasAttribute('data-bind-fetch-redirect')) {
      fetch.redirectUrl = element.getAttribute('data-bind-fetch-redirect')!;
    }
    if (element.hasAttribute('data-bind-fetch-click')) {
      fetch.clickElements = [<HTMLElement>element];
    }
    if (element.tagName == 'FORM') {
      fetch.form = <HTMLFormElement>element;
    }
    fetch.fetch(url, element);
    return true;
  }

  /**
   * data-bind-eachの値をリストとして子エレメントを複製します。
   *
   * @param element 処理対象のエレメント
   * @param 変更した場合はtrue
   */
  static doEach(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      let templates = this.srcEachs.get(element);
      if (templates != null) {
        element.innerHTML = this.srcBeforeEachs.get(element) || '';
        templates.forEach((template: Element) => {
          element.appendChild(HaoriBind.clone(template));
        });
        return true;
      }
      return false;
    }
    let script = element.getAttribute('data-bind-each');
    if (script == null) {
      console.error('data-bind-each属性が指定されていません', element);
      return false;
    } else if (script.indexOf('{{') >= 0) {
      console.error('data-bind-eachに{{}}は不要です', element);
      return false;
    }
    let beforeHtml = this.srcBeforeEachs.get(element) || '';
    let templates = this.srcEachs.get(element);
    if (templates == null) {
      beforeHtml = '';
      templates = [];
      for (let i = 0; i < element.children.length; i++) {
        let child = element.children[i];
        if (child.hasAttribute('data-bind-each-before') || child.hasAttribute('data-bind-each-after')) {
          beforeHtml += child.outerHTML;
        } else {
          templates.push(child);
        }
      }
      element.innerHTML = beforeHtml;
      this.srcBeforeEachs.set(element, beforeHtml);
      this.srcEachs.set(element, templates);
    }
    let params = this.getBindObject(element);
    if (params == null) {
      if (element.innerHTML != beforeHtml) {
        element.innerHTML = beforeHtml;
        return true;
      }
      return false;
    }
    let list: any = null;
    try {
      list = Haori.executeScript(params, script, element);
    } catch (error) {
      if (element.innerHTML != beforeHtml) {
        element.innerHTML = beforeHtml;
        return true;
      }
      return false;
    }
    if (list == null) {
      list = [];
    }
    if (!Array.isArray(list)) {
      console.error('data-bind-eachの実行結果がリストではありません', element, list);
      if (element.innerHTML != beforeHtml) {
        element.innerHTML = beforeHtml;
        return true;
      }
      return false;
    }
    let bindString = this.eachBindStrings.get(element);
    let bindScript = this.eachScripts.get(element);
    let json = JSON.stringify(list);
    if (bindScript == script && bindString == json) {
      return false;
    }
    this.eachScripts.set(element, script);
    this.eachBindStrings.set(element, json);
    let newElements = new Array<Element>();
    list.forEach((object: any, index: number) => {
      if (typeof object == 'object') {
        object['index'] = index;
      }
      let listBind: any;
      if (element.hasAttribute('data-bind-each-arg')) {
        listBind = {};
        listBind[<string>element.getAttribute('data-bind-each-arg')] = object;
        if (typeof object != 'object') {
          listBind['index'] = index;
        }
      } else {
        listBind = object;
      }
      if (templates) {
        let json = JSON.stringify(listBind);
        for (let i = 0; i < templates.length; i++) {
          let template = <HTMLElement>HaoriBind.clone(templates[i]);
          template.setAttribute('data-bind', json);
          newElements.push(template);
        }
      }
    });
    // 差分のみ追加・削除する
    let j = 0;
    let beforeElementCount = 0;
    let afterElementCount = 0;
    for (let i = 0; i < element.children.length; i++) {
      let currentElement = <Element>element.children.item(i);
      if (currentElement.hasAttribute('data-bind-each-before')) {
        beforeElementCount++;
      }
      if (currentElement.hasAttribute('data-bind-each-after')) {
        afterElementCount++;
      }
    }
    let currentElements = new Array<Element>();
    for (let i = beforeElementCount; i < element.children.length - afterElementCount; i++) {
      currentElements.push(<Element>element.children.item(i));
    }
    for (let i = 0; i < currentElements.length; i++) {
      let currentElement = currentElements[i];
      for (; j < newElements.length; j++) {
        let newElement = newElements[j];
        if (currentElement.getAttribute('data-bind') == newElement.getAttribute('data-bind')) {
          j++;
          break;
        }
        Haori.insertBefore(newElement, currentElement);
      }
    }
    for (let i = element.children.length - 1 - afterElementCount; i > beforeElementCount + j - 1; i--) {
      element.children.item(i)?.remove();
    }
    for (; j < newElements.length; j++) {
      let after = element.querySelector('[data-bind-each-after]');
      if (after == null) {
        element.appendChild(newElements[j]);
      } else {
        Haori.insertBefore(newElements[j], after);
      }
    }
    return true;
  }

  /**
   * data-bind-ifの値をスクリプトとして戻り値によって表示・非表示を切り替えます。
   *
   * @param element 処理対象のエレメント
   * @return 変更した場合はtrue
   */
  static doIf(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      if (element.hasAttribute('data-bind-if-false')) {
        let nodes = this.srcIfElements.get(element);
        if (nodes != null) {
          nodes.forEach((node) => {
            element.appendChild(node);
          });
        }
        (<HTMLElement>element).style.display = '';
        element.removeAttribute('data-bind-if-false');
        return true;
      }
      return false;
    }
    let script = element.getAttribute('data-bind-if');
    if (script == null) {
      console.error('data-bind-if属性が指定されていません', element);
      return false;
    } else if (script.indexOf('{{') >= 0) {
      console.error('data-bind-ifに{{}}は不要です', element);
      return false;
    }
    let params = this.getBindObject(element);
    let result: any;
    if (params == null) {
      result = false;
    } else {
      try {
        result = Haori.executeScript(params, script, element);
      } catch (error) {
        result = false;
      }
    }
    if (result === false || result === null || result === undefined) {
      if (element.hasAttribute('data-bind-if-false')) {
        return false;
      }
      if (this.srcIfElements.get(element) == null) {
        let list = new Array<Node>();
        element.childNodes.forEach((node) => {
          list.push(node);
        });
        this.srcIfElements.set(element, list);
      }
      element.innerHTML = '';
      (<HTMLElement>element).style.display = 'none';
      if (element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA' || element.tagName == 'BUTTON') {
        // フォーム要素やボタンはdisabledにする
        if (!element.hasAttribute('disabled')) {
          element.setAttribute('disabled', '');
          element.setAttribute('data-bind-if-disabled', '');
        }
      }
      element.setAttribute('data-bind-if-false', '');
    } else {
      if (!element.hasAttribute('data-bind-if-false')) {
        return false;
      }
      let nodes = this.srcIfElements.get(element);
      if (nodes != null) {
        nodes.forEach((node) => {
          element.appendChild(node);
        });
      }
      (<HTMLElement>element).style.display = '';
      if (element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA' || element.tagName == 'BUTTON') {
        // フォーム要素やボタンはdisabledを解除する
        if (element.hasAttribute('data-bind-if-disabled')) {
          element.removeAttribute('disabled');
          element.removeAttribute('data-bind-if-disabled');
        }
      }
      if (element.hasAttribute('data-bind-if-false')) {
        element.removeAttribute('data-bind-if-false');
      }
    }
    return true;
  }

  /**
   * 対象エレメントの属性のスクリプトを処理します。
   *
   * @param element 処理対象のエレメント
   * @param useSrc 保存された元ソースを使う場合はtrue
   * @param 変更した場合はtrue
   */
  static doScriptAttributes(element: Element, useSrc: boolean): boolean {
    let result = false;
    element.getAttributeNames().forEach((attribute) => {
      if (this.doScriptAttribute(element, attribute, useSrc)) {
        result = true;
      }
    });
    this.removeAttributes.get(element)?.forEach((attribute) => {
      if (this.doScriptAttribute(element, attribute, useSrc)) {
        result = true;
      }
    });
    return result;
  }

  /**
   * 対象エレメントの属性のスクリプトを処理します。
   * data-bind、data-bind-if、data-bind-each属性の場合は何もしません。
   *
   * @param element 処理対象のエレメント
   * @param attribute 処理対象のアトリビュート
   * @param useSrc 保存された元ソースを使う場合はtrue
   * @param 変更した場合はtrue
   */
  static doScriptAttribute(element: Element, attribute: string, useSrc: boolean): boolean {
    if (attribute == 'data-bind' || attribute == 'data-bind-if' || attribute == 'data-bind-each') {
      return false;
    }
    let map = <Map<string, string>>this.srcAttributes.get(element);
    if (map == null) {
      map = new Map();
      this.srcAttributes.set(element, map);
    }
    let text: string | undefined | null = map.get(attribute);
    if (this.isInvalid(element.parentElement)) {
      if (text != null && element.getAttribute(attribute) != text) {
        element.setAttribute(attribute, text);
        return true;
      }
      return false;
    }
    if (!useSrc || text == undefined) {
      text = element.getAttribute(attribute);
      if (text != null) {
        map.set(attribute, text);
      }
    }
    if (text == null || text.indexOf('{{') < 0) {
      return false;
    }
    let params = this.getBindObject(element);
    let newText: any;
    if (params == null || this.isInvalid(element)) {
      newText = text;
    } else {
      newText = this.replaceScript(params, text, element);
      if (newText === true) {
        newText = '';
      }
    }
    let result = false;
    if (newText === false || newText === null || newText === undefined) {
      if (element.hasAttribute(attribute)) {
        let set = this.attributeChangeNodes.get(element);
        if (set == null) {
          set = new Set();
          this.attributeChangeNodes.set(element, set);
        }
        set.add(attribute);
        let attrSet = this.removeAttributes.get(element);
        if (attrSet == null) {
          attrSet = new Set();
          this.removeAttributes.set(element, attrSet);
        }
        attrSet.add(attribute);
        element.removeAttribute(attribute);
        result = true;
      }
    } else {
      if (element.getAttribute(attribute) != newText) {
        let set = this.attributeChangeNodes.get(element);
        if (set == null) {
          set = new Set();
          this.attributeChangeNodes.set(element, set);
        }
        let attrSet = this.removeAttributes.get(element);
        if (attrSet != null) {
          attrSet.delete(attribute);
        }
        set.add(attribute);
        element.setAttribute(attribute, newText);
        result = true;
      }
    }
    return result;
  }

  /**
   * 対象エレメントのテキストコンテンツ内のスクリプトを処理します。
   *
   * @param node 処理対象のエレメント
   * @param useSrc 保存された元ソースを使う場合はtrue
   * @param 変更した場合はtrue
   */
  static doScriptContent(node: Node, useSrc: boolean): boolean {
    if (node.nodeType == Node.ELEMENT_NODE && (<Element>node).tagName == 'SCRIPT') {
      return false;
    }
    if (node.nodeType == Node.ELEMENT_NODE && node.childNodes.length == 1) {
      let element = <HTMLElement>node;
      let text = <string | null>this.srcContents.get(element);
      if (!useSrc || text == null) {
        text = element.textContent;
        this.srcContents.set(element, <string>text);
      }
      if (text != null && text.indexOf('!{{') == 0 && text.indexOf('}}!') == text.length - 3) {
        // HTML として解釈する
        let params = this.getBindObject(<Element>element);
        if (params) {
          let newText = this.replaceScript(params, text, node);
          newText = newText.trim();
          newText = newText.substring(1, newText.length - 1);
          newText = DOMPurify.sanitize(newText);
          if (element.innerHTML == newText) {
            return false;
          } else {
            this.contentChangeNodes.add(element);
            element.innerHTML = newText;
            return true;
          }
        }
      }
    }
    let result = false;
    let params = null;
    for (let i = 0; i < node.childNodes.length; i++) {
      let child = node.childNodes[i];
      if (child.nodeType != Node.TEXT_NODE) {
        continue;
      }
      let text = <string | null>this.srcContents.get(child);
      if (node.nodeType == Node.TEXT_NODE && this.isInvalid(<Element>node.parentElement)) {
        if (text != null && node.textContent != text) {
          node.textContent = text;
          return true;
        }
        return false;
      }
      if (!useSrc || text == null) {
        text = child.textContent;
        this.srcContents.set(child, <string>text);
      }
      if (text == null || text.indexOf('{{') < 0) {
        continue;
      }
      if (params == null) {
        let element: any = node;
        if (node.nodeType != Node.ELEMENT_NODE) {
          element = node.parentElement;
        }
        if (element != null) {
          params = this.getBindObject(<Element>element);
        }
      }
      let newText: any;
      if (params == null) {
        newText = text;
      } else {
        newText = this.replaceScript(params, text, node);
        if (newText === false) {
          newText = text;
        }
      }
      if (newText !== null && typeof newText === 'object') {
        console.log('オブジェクトがテキストとして設定されました', child, newText);
      }
      if (child.textContent != newText) {
        this.contentChangeNodes.add(child);
        child.textContent = newText;
        result = true;
      }
    }
    return result;
  }

  /**
   * テキスト文字列内のスクリプトを実行して戻り値と置換します。
   *
   * @param params 変数として設定するパラメータ
   * @param text テキスト文字列
   * @param element 対象エレメント（エラー時のログ用）
   * @return 置換したテキスト文字列（スクリプトがひとつの場合はその戻り値）
   */
  static replaceScript(params: any, text: string, element: Node | null = null): any {
    let newText: any = null;
    let lastEnd = 0;
    while (true) {
      let start = text.indexOf('{{', lastEnd);
      if (start < 0) {
        if (lastEnd == 0) {
          newText = text;
        } else if (lastEnd >= text.length) {
          break;
        } else {
          if (newText == null) {
            newText = '';
          }
          newText += text.substring(lastEnd);
        }
        break;
      }
      let end = text.indexOf('}}', start);
      if (end < 0) {
        if (lastEnd == 0) {
          newText = text;
        } else {
          if (newText == null) {
            newText = '';
          }
          newText += text.substring(lastEnd);
        }
        break;
      }
      if (lastEnd < start) {
        if (newText == null) {
          newText = '';
        }
        newText += text.substring(lastEnd, start);
      }
      let script = text.substring(start + 2, end);
      let result: any;
      try {
        result = Haori.executeScript(params, script, element);
      } catch (error) {
        return text;
      }
      if (result == null) {
        return false;
      }
      if (newText == null) {
        newText = result;
      } else {
        newText += result;
      }
      lastEnd = end + 2;
    }
    return newText;
  }

  /**
   * 対象エレメントおよびその先祖エレメントに割り当てられたオブジェクトを取得します。
   * パラメータ名が同じ場合は子エレメントのものが優先されます。
   *
   * @param element 対象エレメント
   * @return エレメントに割り当てられたオブジェクト
   */
  static getBindObject(element: Element): any {
    if (this.isInvalid(element.parentElement)) {
      return null;
    }

    let object: any | null = null;
    let bindString = (<Element>element).getAttribute('data-bind');
    if (bindString != null && bindString.length > 0 && (bindString.startsWith('[') || bindString.startsWith('{'))) {
      object = JSON.parse(bindString);
    }
    if ((<Element>element).hasAttribute('data-bind-default')) {
      let defaultString = (<Element>element).getAttribute('data-bind-default');
      if (defaultString != null && defaultString.length > 0) {
        let defaultObject = JSON.parse(defaultString);
        object = { ...defaultObject, ...object };
      }
    }

    let parent = element.parentElement;
    if (parent == null || parent.tagName == 'HTML') {
      return object || {};
    }
    let parentObject = this.getBindObject(parent);
    if (object == null && parentObject == null) {
      return null;
    }
    return { ...parentObject, ...object };
  }

  /**
   * 属性の値を変更します。変更元の値にスクリプトが含まれている場合は属性を付け替えません。
   *
   * @param element 対象エレメント
   * @return 変更した場合はtrue
   */
  static transfer(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      return false;
    }
    let transfers: Array<string> | null = null;
    if (element.hasAttribute('data-bind-transfer')) {
      transfers = (<string>element.getAttribute('data-bind-transfer')).split(' ');
    }
    if (transfers == null || transfers.length != 2) {
      transfers = null;
      console.error('data-bind-transfer属性の値は（変換元属性名）" "（変換先属性名）で記述してください');
      return false;
    }
    if (!element.hasAttribute(transfers[0])) {
      return false;
    }
    let result = false;
    let value = element.getAttribute(transfers[0]);
    if (value == null) {
      if (element.hasAttribute(transfers[1])) {
        result = true;
      }
    } else {
      if (value.indexOf('{{') >= 0) {
        return false;
      }
      setTimeout(() => {
        element.setAttribute(transfers[1], value);
      });
      result = true;
    }
    if (result && element.tagName == 'SOURCE') {
      let video = <HTMLVideoElement>element.closest('video');
      if (video) {
        video.load();
      }
    }
    return result;
  }
}

HaoriBind.attributeObserver.observe(document, {
  attributes: true,
  subtree: true,
  attributeOldValue: true,
});

HaoriBind.contentObserver.observe(document, {
  characterData: true,
  subtree: true,
});

HaoriBind.childObserver.observe(document, {
  childList: true,
  subtree: true,
});

document.addEventListener('DOMContentLoaded', () => {
  HaoriBind.bind(document.head);
  HaoriBind.bind(document.body);
});

window.addEventListener('pageshow', (event) => {
  let entries = performance.getEntriesByType('navigation');
  if (entries.length == 0) {
    return;
  }
  if (event.persisted) {
    // キャッシュから表示された場合
    document.querySelectorAll('[data-bind-fetch-current]').forEach((element) => {
      element.removeAttribute('data-bind-fetch-current');
    });
    HaoriBind.bind(document.head);
    HaoriBind.bind(document.body);
  }
});
